import { Box, List, Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useBoolean } from 'usehooks-ts';

import { StatusProgressDots } from '@/components/DataDisplay/Indicator/StatusProgressDots';
import { MinorIdentityCheck } from '@/components/PageForm/MoreInfoForm/components/MinorIdentityCheck';
import { MoreInfoFormContainer } from '@/components/PageForm/MoreInfoForm/MoreInfoForm.styled';
import { TermsAndConditionsDrawer } from '@/components/PageForm/TermsAndConditions/TermsAndConditionsDrawer';

import { DOCUMENT_COMPLETED_STATUSES } from '@/constants/document';
import { MINOR_IDENTITY_DOCUMENTS } from '@/constants/user';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import useEligibleChangedPopup from '@/hooks/useEligibleChangedPopup';
import { useWindowFocus } from '@/hooks/useWindowFocus';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { notifyMinorDataChanged } from '@/redux/operation';
import {
  selectBasicInfoStatus,
  selectOperationData,
  selectOperationUser,
  selectSigningDocuments,
} from '@/redux/operation/selectors';

import { DocumentSection, DocumentStatus } from '@/types/document';

import { compact } from '@/utils/collection';
import { getMinorIdentityDocumentCheckStatus } from '@/utils/document';

import { MinorAdditionalID } from './components/MinorAdditionalID';
import { MinorBasicInfo } from './components/MinorBasicInfo';
import { MinorDocumentSigning } from './components/MinorDocumentSigning';
import {
  getMinorAddressStatus,
  MinorProofOfAddress,
} from './components/MinorProofOfAddress';
import { MinorWarrantyOfAccuracyAccordion } from './components/MinorWarrantyOfAccuracyAccordion';
import { isMinorBasicInfoFullyComplete } from './utils';

const isStatusCompleted = (status: DocumentStatus | undefined) =>
  status === DocumentStatus.InReview || status === DocumentStatus.Approved;

export function MinorMoreInformationForm() {
  const dispatch = useAppDispatch();

  const parentUser = useAppSelector(selectLoggedInUser);
  const minorUser = useAppSelector(selectOperationUser);
  const minorBasicInfoStatus = useAppSelector(selectBasicInfoStatus);
  const { requiredDocumentSections } = useAppSelector(selectOperationData);
  const { documentsSigningStatus, remainingDocsToSign } = useAppSelector(
    selectSigningDocuments,
  );

  const {
    value: isProofOfAddressOpen,
    setTrue: openProofOfAddressDrawer,
    setFalse: closeProofOfAddressDrawer,
  } = useBoolean(false);

  const isMissingIdentityDoc = useMemo(() => {
    const documents = minorUser?.documents || [];
    const missingDocuments = MINOR_IDENTITY_DOCUMENTS.filter(
      doc => !documents.some(d => d.type === doc),
    );

    return missingDocuments.length !== 0;
  }, [minorUser]);

  const isBasicInfoFullyComplete = isMinorBasicInfoFullyComplete(
    minorBasicInfoStatus,
    minorUser,
  );

  const isCompleted = useMemo(() => {
    const areRequiredDocumentsCompleted = requiredDocumentSections
      .filter(section => section.type !== DocumentSection.DocumentSign)
      .every(section => isStatusCompleted(section.status));

    return (
      isBasicInfoFullyComplete &&
      areRequiredDocumentsCompleted &&
      DOCUMENT_COMPLETED_STATUSES.includes(documentsSigningStatus) &&
      remainingDocsToSign === 0
    );
  }, [
    isBasicInfoFullyComplete,
    requiredDocumentSections,
    documentsSigningStatus,
    remainingDocsToSign,
  ]);

  const isDocumentSigningDisabled = useMemo(() => {
    const isAdditionalIdPending = requiredDocumentSections.some(
      section =>
        [
          DocumentSection.SecondID,
          DocumentSection.SecondCitizenshipID,
        ].includes(section.type) && section.status === DocumentStatus.Pending,
    );

    let isDisabledPOA = false;
    if (!minorUser?.addressDetailed?.sameAsAdult) {
      const proofOfAddressSection = requiredDocumentSections.find(
        section => section.type === DocumentSection.ProofOfAddress,
      );
      isDisabledPOA = proofOfAddressSection?.status === DocumentStatus.Pending;
    }

    // If Identity Check, basicInfoStatus, POA, Addition ID is not completed
    // Disable the document signing
    return (
      minorBasicInfoStatus === undefined ||
      isDisabledPOA ||
      isAdditionalIdPending ||
      isMissingIdentityDoc
    );
  }, [
    minorBasicInfoStatus,
    requiredDocumentSections,
    isMissingIdentityDoc,
    minorUser,
  ]);

  useEffect(() => {
    window.focus();
  }, []);

  useWindowFocus(() => {
    dispatch(notifyMinorDataChanged());
  });

  useEligibleChangedPopup(
    requiredDocumentSections,
    minorUser,
    openProofOfAddressDrawer,
  );

  const addressSection = requiredDocumentSections.find(
    section => section.type === DocumentSection.ProofOfAddress,
  );

  const minorStatusDots = useMemo(
    () =>
      compact([
        ...[getMinorIdentityDocumentCheckStatus(minorUser?.documents)],
        minorBasicInfoStatus ?? DocumentStatus.Pending,
        ...[getMinorAddressStatus(minorUser, addressSection)],
        documentsSigningStatus,
      ]),
    [minorBasicInfoStatus, minorUser, addressSection, documentsSigningStatus],
  );

  if (!minorUser) return null;

  return (
    <>
      <Stack
        justifyContent="center"
        direction={'column'}
        width={'100%'}
        sx={{ mt: 2 }}
      >
        <StatusProgressDots dots={minorStatusDots} />
      </Stack>
      <MoreInfoFormContainer data-testid="more-information-form-minor">
        <List>
          {/* Identity Check Section */}
          <MinorIdentityCheck />

          {/* Basic Information Section (includes Consents + Other information) */}
          <MinorBasicInfo />

          {/* Proof of Address  */}
          <MinorProofOfAddress
            open={isProofOfAddressOpen}
            onOpen={openProofOfAddressDrawer}
            onClose={closeProofOfAddressDrawer}
          />

          {/* Additional Id  */}
          <MinorAdditionalID />

          {/* Document Signing Section */}
          <MinorDocumentSigning disabled={isDocumentSigningDisabled} />

          <Box mt={1.5}>
            <MinorWarrantyOfAccuracyAccordion disabled={!isCompleted} />
          </Box>
        </List>

        <TermsAndConditionsDrawer open={!parentUser?.termsAndConditions} />
      </MoreInfoFormContainer>
    </>
  );
}
